import { DateTime } from 'luxon'
import type { FilterOperation } from '../../OperationTypes.js'
import type { MetadataFieldDescriptor } from '../../MetadataFields.js'
import { isValueListConfig } from '../../Operations/Config/ValueListConfig.js'
import { isDateRangeConfig } from '../../Operations/Config/DateRangeConfig.js'
import { ValueListSelector } from './ValueListSelector.js'
import { isMinMaxTimeConfig } from '../../Operations/Config/MinMaxTimeConfig.js'
import { isSingleValueConfig } from '../../Operations/Config/SingleValueConfig.js'
import type { FilterOperationKey } from '../../OperationRegistry.ts'
import type { VisualizationSampleContainer } from '../../VisualizationSampleContainer.js'

export function FilterConfigEditor({
  operation,
  config,
  onChange,
  field,
  samples
}: {
  operation?: Omit<FilterOperation<FilterOperationKey, unknown>, 'apply'>
  config: unknown
  onChange: (c: unknown) => void
  field?: MetadataFieldDescriptor
  samples: VisualizationSampleContainer[]
}) {
  if (!operation || !field) return null

  if ((operation.key === 'filter-in' || operation.key === 'filter-not-in') && isValueListConfig(config)) {
    return (
      <div className="flex-1 min-w-[200px]">
        <ValueListSelector
          samples={samples}
          field={field}
          multiselect={true}
          value={config.values}
          onChange={(values) => onChange({ ...config, values })}
        />
      </div>
    )
  }

  if (operation.key === 'is-unset' || operation.key === 'is-not-unset') {
    return <></>
  }

  if (operation.key === 'contains' && isSingleValueConfig(config)) {
    return (
      <input
        type="text"
        className="input input-sm input-bordered"
        value={config.value ?? ''}
        onChange={(e) => onChange({ ...config, value: e.target.value })}
      />
    )
  }

  // date range filter
  if (operation.key === 'filter-date-range' && isDateRangeConfig(config)) {
    return (
      <div className="flex gap-1 items-center">
        <input
          type="date"
          className="input input-sm input-bordered"
          value={(config.from ? DateTime.fromISO(config.from).toISODate() : '') ?? ''}
          onChange={(e) =>
            onChange({
              ...config,
              from: e.target.value ? DateTime.fromISO(e.target.value).toISO() : undefined
            })
          }
        />
        <span className="text-xs text-gray-400">to</span>
        <input
          type="date"
          className="input input-sm input-bordered"
          value={(config.to ? DateTime.fromISO(config.to).toISODate() : '') ?? ''}
          onChange={(e) =>
            onChange({
              ...config,
              to: e.target.value ? DateTime.fromISO(e.target.value).toISO() : undefined
            })
          }
        />
      </div>
    )
  }

  // duration range filter
  if (operation.key === 'filter-duration' && isMinMaxTimeConfig(config)) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          className="input input-sm input-bordered w-20"
          placeholder="Min"
          value={config.min ?? ''}
          onChange={(e) =>
            onChange({
              ...config,
              min: e.target.value === '' ? undefined : Number(e.target.value)
            })
          }
        />
        <input
          type="number"
          className="input input-sm input-bordered w-20"
          placeholder="Max"
          value={config.max ?? ''}
          onChange={(e) =>
            onChange({
              ...config,
              max: e.target.value === '' ? undefined : Number(e.target.value)
            })
          }
        />
        <select
          className="select select-sm select-bordered"
          value={config.unit}
          onChange={(e) => onChange({ ...config, unit: e.target.value as 'days' | 'months' | 'years' })}
        >
          <option value="days">Days</option>
          <option value="months">Months</option>
          <option value="years">Years</option>
        </select>
      </div>
    )
  }

  // fallback
  return <span className="text-xs text-gray-400">No editor yet. Please contact support.</span>
}
